import type { CSSProperties } from 'react';
import { useCallback, useMemo } from 'react';
import type { Column } from '@tanstack/react-table';
import { cn } from '@/lib/utils';
import {
  TableHead,
  TableRow,
  TableHeader as ShadcnTableHeader,
} from '@/components/ui/table';
import type { ColumnConfig, TableHeaderProps, TableRowData } from '../../types';
import { isGroupColumn } from '../../types';
import {
  getLeafColumns,
  getVisualColumnCountForColumn,
} from '../../utils/columnGroupUtils';
import { buildColumnSizeStyle, readColumnSize } from '../../utils/columnSizing';
import { HeaderCellContent } from './HeaderCellContent';

export function GroupedHeader<TRow extends TableRowData>({
  table,
  columns,
  filters,
  onFilterChange,
  sort,
  onSortChange,
  enableSorting,
  enableRowExpansion,
  tableLayout = 'fixed',
}: TableHeaderProps<TRow>) {
  const leafColumns = getLeafColumns(columns);

  const leafColumnsById = useMemo(() => {
    const map = new Map<string, Column<TRow, unknown>>();
    table.getLeafHeaders().forEach(header => map.set(header.id, header.column));
    return map;
  }, [table]);

  const cellStyle = useCallback(
    (columnId: string): CSSProperties | undefined => {
      const column = leafColumnsById.get(columnId);
      return column
        ? buildColumnSizeStyle(tableLayout, readColumnSize(column))
        : undefined;
    },
    [leafColumnsById, tableLayout]
  );

  // Group span width keeps the grouping cell aligned with its leaves under
  // table-fixed; under auto the span derives from its children, so skip it.
  const groupStyle = useCallback(
    (column: ColumnConfig<TRow>): CSSProperties | undefined => {
      if (
        tableLayout === 'auto' ||
        !isGroupColumn(column) ||
        !column.children
      ) {
        return undefined;
      }
      const totalWidth = getLeafColumns(column.children).reduce(
        (sum, child) =>
          sum + (leafColumnsById.get(child.id)?.getSize() ?? child.width ?? 0),
        0
      );
      return totalWidth > 0 ? { width: totalWidth } : undefined;
    },
    [leafColumnsById, tableLayout]
  );

  return (
    <ShadcnTableHeader className="sticky top-0 z-20 bg-muted [&_tr]:border-0">
      {/* Row 1: Grouping headers only */}
      <TableRow className="overflow-visible bg-muted hover:bg-muted h-8">
        {enableRowExpansion && (
          <TableHead
            className="w-10 px-2 py-1 th-border-b"
            aria-hidden="true"
          />
        )}
        {columns.map(column => {
          if (isGroupColumn(column) && column.children) {
            return (
              <TableHead
                key={column.id}
                colSpan={getVisualColumnCountForColumn(column)}
                style={groupStyle(column)}
                className={cn(
                  // The empty cells around a group carry no divider, so the
                  // group draws its own on both edges.
                  'th-divider th-divider-start w-full overflow-visible bg-muted text-center py-1 th-border-b',
                  column.headerClassName
                )}
              >
                <span className="text-xs font-semibold text-foreground">
                  {column.header}
                </span>
              </TableHead>
            );
          }

          return (
            <TableHead
              key={column.id}
              style={cellStyle(column.id)}
              className="w-full bg-muted py-1 th-border-b"
              aria-hidden="true"
            />
          );
        })}
      </TableRow>

      {/* Row 2: Main headers (leaf columns) */}
      <TableRow className="overflow-visible bg-muted hover:bg-muted">
        {enableRowExpansion && (
          <TableHead
            className="th-divider w-10 px-2 bg-muted border-b border-border"
            aria-hidden="true"
          />
        )}
        {leafColumns.map(column => (
          <TableHead
            key={column.id}
            style={cellStyle(column.id)}
            className={cn(
              'th-divider w-full overflow-visible bg-muted border-b border-border',
              column.headerClassName
            )}
          >
            <HeaderCellContent
              column={column}
              headerText={column.header}
              filters={filters}
              onFilterChange={onFilterChange}
              sort={sort}
              onSortChange={onSortChange}
              enableSorting={enableSorting ?? false}
            />
          </TableHead>
        ))}
      </TableRow>
    </ShadcnTableHeader>
  );
}
